import React, { useState, useEffect } from "react";
import './forgot.css';

export default function ForgotPasswordRequest({ showAlert }) {
  const [username, setUsername] = useState("");
  const [identifier, setIdentifier] = useState(""); // email or phone depending on tab
  const [channel, setChannel] = useState("email"); // email | sms
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [uErr, setUErr] = useState("");
  const [idErr, setIdErr] = useState("");
  const [toastQueue, setToastQueue] = useState([]); // {id,type,msg}

  // Legacy flow: no normalization, backend expects raw email or phone and username.

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setInfo("");
    setUErr("");
    setIdErr("");

    // Validate both fields mandatory: username and contact (email/phone)
    let valid = true;
    if (!username.trim()) { setUErr("Username is required"); valid = false; }
    if (!identifier.trim()) { setIdErr("Email or mobile is required"); valid = false; }
    if (!valid) return;

    setLoading(true);
    try {
      // Decide payload identifier and channel automatically if possible
      const payloadIdentifier = identifier.trim();
      const payloadChannel = channel; // trust user tab selection

      // Legacy endpoint expects: type, email OR phone, username
      const payload = payloadChannel === 'email'
        ? { type: 'email', email: payloadIdentifier, username: username.trim() }
        : { type: 'sms', phone: payloadIdentifier, username: username.trim() };

      // Debug (non-invasive): helps verify what is sent during development
      try { console.debug('[ForgotPassword] Payload', payload); } catch(_) {}

      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/usr-forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json", 'Origin': window.location.origin },
        body: JSON.stringify(payload)
      });
      const data = await res.json().catch(() => ({}));

      if (res.ok) {
        const successMsg = data?.message || 'OTP sent successfully.';
        setInfo(successMsg);
        enqueueToast('success', successMsg);
        showAlert?.(successMsg);
        // store username for verify step
        try { localStorage.setItem('forgot_username', username.trim()); } catch(_) {}
        window.location.href = '/forgot-password/verify';
      } else {
        const msg = (data?.message || '').toString();
        setError(msg || 'Failed to send OTP.');
        const finalMsg = msg || 'Failed to send OTP.';
        setError(finalMsg);
        enqueueToast('error', finalMsg);
      }
    } catch (err) {
      setError("Network error. Please try again.");
      const netMsg = "Network error. Please try again.";
      setError(netMsg);
      enqueueToast('error', netMsg);
    } finally {
      setLoading(false);
    }
  };

  const enqueueToast = (type, msg) => {
    const id = Date.now() + Math.random();
    setToastQueue(q => [...q, { id, type, msg }]);
    setTimeout(() => {
      setToastQueue(q => q.filter(t => t.id !== id));
    }, 3500);
  };

  const usernameClass = `fp-input ${uErr ? 'fp-input-error' : ''}`;
  const identifierClass = `fp-input ${idErr ? 'fp-input-error' : ''}`;

  useEffect(() => { if (error && error.length && !toastQueue.find(t => t.msg === error)) enqueueToast('error', error); }, [error]);

  return (
    <div className="fp-wrap">
      <div className="fp-steps" aria-hidden="true">
        <div className="fp-step active">Account</div>
        <div className="fp-step">OTP</div>
        <div className="fp-step">Reset</div>
      </div>
      <div className="fp-tabs">
        <button type="button" className={`fp-tab ${channel === 'email' ? 'active' : ''}`} onClick={() => setChannel('email')}>Email</button>
        <button type="button" className={`fp-tab ${channel === 'sms' ? 'active' : ''}`} onClick={() => setChannel('sms')}>SMS</button>
      </div>
      <h3 className="fp-title">Forgot password</h3>
      {error && <div className="fp-error" role="alert" aria-live="assertive">{error}</div>}
      {info && <div className="fp-info" role="status" aria-live="polite">{info}</div>}
      <form onSubmit={onSubmit} noValidate className="fp-form">
        <label className="fp-label">Username</label>
        <input
          className={usernameClass}
          type="text"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          placeholder="Username"
        />
        {uErr && <div className="fp-help-err">{uErr}</div>}

        <label className="fp-label">{channel === 'email' ? 'E-mail' : 'Phone Number'}</label>
        <input
          className={identifierClass}
          type="text"
          value={identifier}
          onChange={(e) => setIdentifier(e.target.value)}
          placeholder={channel === 'email' ? 'Input valid email' : '01XXXXXXXXX'}
        />
        {idErr && <div className="fp-help-err">{idErr}</div>}
        <button disabled={loading} className="fp-submit">
          {loading && <span className="fp-spinner" />}
          {loading ? "Sending..." : "Send OTP"}
        </button>
      </form>
      <div className="fp-note">We’ll send a code if the account exists.</div>
      {toastQueue.length > 0 && (
        <div className="fp-toast" role="status" aria-live="polite">
          {toastQueue.map(t => (
            <div key={t.id} className={`fp-toast-item ${t.type === 'success' ? 'fp-toast-success' : 'fp-toast-error'}`}>
              <span className="fp-toast-icon">{t.type === 'success' ? '✓' : '⚠'}</span>
              <span>{t.msg}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
